import type { Notification } from '@ralph/contracts'
import { describeIssue } from '@/i18n/errors.ts'
import type { Messages } from '@/i18n/messages/index.ts'

/**
 * What a notification SAYS, in the language of whoever is reading it (PRD-27 LOC-035).
 *
 * The row carries both halves: an English `subject`/`body` written when the event happened,
 * and — since `0014` — the key of each sentence with the values it interpolates. This picks
 * the key when there is one, which is what makes the inbox follow the reader rather than the
 * actor: a maker working in Portuguese asks to share an agent, and the administrator who
 * opens the request reads it in English, because that is the language their tab is set to.
 * Neither of them chose the other's.
 *
 * ── Why the English prose is still here ──
 *
 * Three cases fall back to it, and none of them is a defect:
 *
 *  1. A row written before `0014` has no key at all. It cannot be retrofitted — nothing knows
 *     what those sentences were made of — and its stored prose is its record.
 *  2. A row written by a pod that has learned a key this build has not. The alternative to
 *     prose would be a blank line where a notice was, which is the deploy-order failure
 *     `messageKey` was designed to avoid.
 *  3. A body that is somebody's own words: a suspension reason, typed by an administrator.
 *     That one has no key on purpose — wrapping content in a sentence of ours would say less
 *     than showing it (D-17).
 *
 * `describeIssue` is reused rather than reimplemented: "look a key up in the catalogue, render
 * it, and say `null` if this build does not know it" is exactly the same question the error
 * path asks, and it already refuses to guess.
 */
export const notificationText = (t: Messages, notification: Notification): { subject: string; body: string } => ({
  subject:
    describeIssue(t, { messageKey: notification.subjectKey, params: notification.subjectParams }) ??
    notification.subject,
  body: describeIssue(t, { messageKey: notification.bodyKey, params: notification.bodyParams }) ?? notification.body
})
